import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, FileText, Info } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import LiveInterestIndicator from '@/components/LiveInterestIndicator';

const ServiceTemplate = ({
  title,
  description,
  benefits,
  documents,
  process,
  timeline,
  pricing,
  faqs,
  heroImage,
  detailedDescription,
  keywords,
  additionalInfo,
}) => {
  // WhatsApp function with pre-filled message
  const openWhatsApp = () => {
    const message = `Hello, I am interested in ${title}.\nPlease share complete details, pricing, documents required and next steps.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/message/PZZL5GZ65EAIC1?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };
  
  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": title,
    "description": description,
    "provider": {
      "@type": "ProfessionalService",
      "name": "NextGen Filling Association",
      "telephone": "+91-9509363261",
      "email": "info@nextgenfilling.com"
    },
    "offers": {
      "@type": "Offer",
      "price": pricing,
      "priceCurrency": "INR"
    }
  };
  
  return (
    <>
      <Helmet>
        <title>{title} | NextGen Filling Association</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        <meta property="og:title" content={`${title} | NextGen Filling`} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <div className="min-h-screen" data-testid="service-page">
      <section
        className="relative h-[400px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.9), rgba(15, 23, 42, 0.7)), url('${heroImage || 'https://images.unsplash.com/photo-1649209979970-f01d950cc5ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHx0YXglMjBmaWxpbmclMjBwYXBlcndvcmslMjBjYWxjdWxhdG9yJTIwZGVzayUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcyMjUwNTE4fDA&ixlib=rb-4.1.0&q=85'}')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {title}
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            {description}
          </p>
          
          {/* Live Interest Indicator */}
          <div className="flex justify-center mt-6">
            <LiveInterestIndicator serviceName={title} />
          </div>
        </div>
      </section>

      {/* Detailed Description Section */}
      {detailedDescription && (
        <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="flex items-start gap-4 mb-6">
                <Info className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    About This Service
                  </h2>
                  <div className="prose prose-lg max-w-none text-slate-700" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {detailedDescription}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Key Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3" data-testid={`benefit-${index}`}>
                <CheckCircle className="text-amber-500 flex-shrink-0 mt-1" size={20} />
                <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {benefit}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Additional Information Section */}
      {additionalInfo && (
        <section className="py-12 bg-blue-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 border-l-4 border-blue-600">
              <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Important Information
              </h3>
              <div className="text-slate-700 space-y-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                {additionalInfo}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <FileText className="inline-block mr-2 text-blue-800" size={32} />
                Required Documents
              </h2>
              <ul className="space-y-3">
                {documents.map((doc, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-blue-800 flex-shrink-0 mt-1" size={18} />
                    <span className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {doc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <Clock className="inline-block mr-2 text-amber-600" size={32} />
                Process & Timeline
              </h2>
              <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
                <p className="text-2xl font-bold text-amber-600 mb-2">{timeline}</p>
                <p className="text-sm text-slate-500">Estimated completion time</p>
              </div>
              <div className="space-y-4">
                {process.map((step, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-800 text-white rounded-full flex-shrink-0 text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {step}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Pricing
          </h2>
          <div className="bg-gradient-to-r from-slate-900 to-blue-900 text-white p-12 rounded-2xl text-center shadow-xl">
            <p className="text-5xl font-bold mb-4">{pricing}</p>
            <p className="text-slate-300 mb-6">All-inclusive package</p>
            <Button 
              onClick={openWhatsApp}
              className="bg-green-500 text-white hover:bg-green-600 rounded-lg px-8 py-4 font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2 mx-auto" 
              data-testid="service-cta-button"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Get Started on WhatsApp
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg px-6 border border-slate-200">
                <AccordionTrigger className="text-left font-semibold text-slate-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-lg text-slate-300 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            Contact our experts today for a free consultation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={openWhatsApp}
              className="bg-green-500 text-white hover:bg-green-600 rounded-lg px-8 py-4 font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Chat on WhatsApp
            </Button>
            <a href="tel:+919509363261">
              <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-lg px-8 py-4 font-medium">
                Call +91 9509363261
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ServiceTemplate;